import { useState } from "react";
import type { Flight } from "../../../mocks/flights";

interface FlightCardProps {
  flight: Flight;
}

function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m > 0 ? `${m}m` : ""}`.trim();
}

function formatPrice(price: number, currency: string): string {
  if (currency === "PKR") {
    return `PKR ${price.toLocaleString()}`;
  }
  return `${currency} ${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function FlightCard({ flight }: FlightCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#0c2461]/30 transition-all">
      <div className="p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Airline */}
          <div className="flex items-center gap-3 min-w-[160px]">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: flight.airlineColor }}
            >
              {flight.airlineCode}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-800 leading-tight">{flight.airline}</p>
              <p className="text-xs text-gray-400 mt-0.5">{flight.id} · {flight.aircraft}</p>
            </div>
          </div>

          {/* Route Timeline */}
          <div className="flex items-center gap-4 flex-1 justify-center min-w-[220px]">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900">{flight.departure}</p>
              <p className="text-sm font-semibold text-gray-600">{flight.fromCode}</p>
              <p className="text-xs text-gray-400">{flight.from}</p>
            </div>
            <div className="flex-1 flex flex-col items-center gap-1 min-w-[80px]">
              <p className="text-xs text-gray-400">{formatDuration(flight.durationMin)}</p>
              <div className="relative w-full flex items-center">
                <div className="h-px bg-gray-200 flex-1"></div>
                {flight.stops === 0 ? (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-flight-takeoff-line text-[#0c2461] text-sm"></i>
                  </div>
                ) : (
                  <div className="w-2.5 h-2.5 rounded-full border-2 border-[#0c2461] bg-white flex-shrink-0"></div>
                )}
                <div className="h-px bg-gray-200 flex-1"></div>
              </div>
              <p className={`text-xs font-medium ${flight.stops === 0 ? "text-emerald-600" : "text-amber-600"}`}>
                {flight.stops === 0 ? "Non-stop" : `${flight.stops} Stop${flight.stops > 1 ? "s" : ""}${flight.stopCity ? ` (${flight.stopCity})` : ""}`}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900">{flight.arrival}</p>
              <p className="text-sm font-semibold text-gray-600">{flight.toCode}</p>
              <p className="text-xs text-gray-400">{flight.to}</p>
            </div>
          </div>

          {/* Baggage + Tags */}
          <div className="flex flex-col gap-1 items-center min-w-[80px]">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-briefcase-line text-xs"></i></div>
              {flight.baggage}
            </div>
            {flight.refundable && (
              <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Refundable</span>
            )}
            {flight.seatsLeft && flight.seatsLeft <= 5 && (
              <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">{flight.seatsLeft} seats left</span>
            )}
          </div>

          {/* Price + Book */}
          <div className="flex flex-col items-end gap-2 min-w-[150px]">
            {flight.originalPrice && (
              <p className="text-xs text-gray-400 line-through">{formatPrice(flight.originalPrice, flight.currency)}</p>
            )}
            <p className="text-xl font-bold text-[#0c2461]">{formatPrice(flight.price, flight.currency)}</p>
            <p className="text-xs text-gray-400">per person</p>
            <button className="bg-[#0c2461] hover:bg-[#0a1f52] text-white px-5 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap w-full">
              Book Now
            </button>
          </div>
        </div>

        {/* Expand Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1 text-xs text-[#0c2461] font-medium cursor-pointer hover:underline"
        >
          <span>{expanded ? "Hide Details" : "Flight Details"}</span>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-sm`}></i>
          </div>
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Departure</p>
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-flight-takeoff-line text-[#0c2461] text-xs"></i></div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{flight.departure} · {flight.fromCode}</p>
                  <p className="text-xs text-gray-500">{flight.from}</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Arrival</p>
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-flight-land-line text-[#0c2461] text-xs"></i></div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{flight.arrival} · {flight.toCode}</p>
                  <p className="text-xs text-gray-500">{flight.to}</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Flight Info</p>
              <div className="space-y-1">
                <p className="text-xs text-gray-600"><span className="font-medium">Aircraft:</span> {flight.aircraft}</p>
                <p className="text-xs text-gray-600"><span className="font-medium">Flight:</span> {flight.flightNumber || flight.id}</p>
                <p className="text-xs text-gray-600"><span className="font-medium">Duration:</span> {formatDuration(flight.durationMin)}</p>
                <p className="text-xs text-gray-600"><span className="font-medium">Baggage:</span> {flight.baggage} included</p>
                {flight.currency !== "PKR" && (
                  <p className="text-xs text-gray-600"><span className="font-medium">Currency:</span> {flight.currency}</p>
                )}
              </div>
            </div>
          </div>
          {flight.stops > 0 && flight.stopCity && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line text-sm"></i></div>
                <span>This flight has a layover in <strong>{flight.stopCity}</strong>. Check-in baggage is transferred automatically.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
